import { useQuery } from '@tanstack/react-query';
import { authenticatedApiClient } from '../api/client';
import type { ResourcePermissions as ResourcePermissionsType } from '@sizzlenz/shared';

/**
 * Query key factory for resource permissions
 */
export const resourcePermissionsKeys = {
  all: ['resource-permissions'] as const,
  resource: (resourceType: string, resourceId: number) =>
    [...resourcePermissionsKeys.all, resourceType, resourceId] as const,
};

/**
 * Fetch user's permissions for a resource
 */
async function fetchResourcePermissions(
  resourceType: 'brand' | 'entity' | 'collection' | 'initiative',
  resourceId: number
): Promise<ResourcePermissionsType> {
  // Map resource type to API endpoint path
  // 'brand' is mapped to 'entities' for backward compatibility
  const apiPath =
    resourceType === 'brand' || resourceType === 'entity'
      ? `entities/${resourceId}/permissions`
      : `${resourceType}s/${resourceId}/permissions`; // Future: collections, initiatives

  return authenticatedApiClient.get(apiPath).json<ResourcePermissionsType>();
}

/**
 * Extended resource permissions interface with helper function
 */
export interface ResourcePermissions extends ResourcePermissionsType {
  /** Helper function to check if user has a specific permission */
  hasPermission: (permission: string) => boolean;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
}

/**
 * Hook to get current user's permissions for any resource
 *
 * Features:
 * - Fetches user's role and permissions for a resource
 * - 5-minute staleTime for stable data
 * - Provides hasPermission helper function
 * - Returns loading and error states
 *
 * @param resourceType - Type of resource ('entity', 'brand', 'collection', 'initiative')
 * @param resourceId - ID of the resource
 * @returns Resource permissions with helper function and states
 *
 * @example
 * ```tsx
 * const permissions = useResourcePermissions('entity', 123);
 *
 * if (permissions.isLoading) return <Spinner />;
 * if (permissions.error) return <Error />;
 *
 * if (permissions.hasPermission('edit_products')) {
 *   return <EditButton />;
 * }
 * ```
 */
export function useResourcePermissions(
  resourceType: 'entity' | 'brand' | 'collection' | 'initiative',
  resourceId: number
): ResourcePermissions {
  const query = useQuery({
    queryKey: resourcePermissionsKeys.resource(resourceType, resourceId),
    queryFn: () => fetchResourcePermissions(resourceType, resourceId),
    staleTime: 10 * 1000,
    enabled: !!resourceId && !!resourceType,
  });

  /**
   * Helper function to check if user has a specific permission
   */
  const hasPermission = (permission: string): boolean => {
    if (!query.data) return false;

    // Platform admins have all permissions
    if (query.data.isPlatformAdmin) return true;

    // Check if permission is in the permissions array
    return query.data.permissions.includes(permission);
  };

  return {
    role: query.data?.role ?? null,
    permissions: query.data?.permissions ?? [],
    isPlatformAdmin: query.data?.isPlatformAdmin ?? false,
    hasPermission,
    isLoading: query.isLoading,
    error: query.error as Error | null,
  };
}
